import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API, { SESSION_EXPIRED_KEY, RETURN_TO_KEY } from '../services/api';
import MFAVerification from '../components/MFAVerification';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaTicket, setMfaTicket] = useState('');
  const navigate = useNavigate();
  const { setSession } = useAuth();
  // Set by the API client when a signed-in request came back 401.
  const [expired] = useState(() => { try { return sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1'; } catch { return false; } });

  // Where to go after signing in: back to the page the session ended on, unless setup is due.
  const finishLogin = (data: { user: any; token: string }) => {
    setSession(data.user, data.token);
    let returnTo = '/';
    try {
      returnTo = sessionStorage.getItem(RETURN_TO_KEY) || '/';
      sessionStorage.removeItem(RETURN_TO_KEY);
      sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    } catch { /* storage unavailable */ }
    navigate(data.user?.mustChangePassword === true ? '/setup' : returnTo, { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Try to login
      const response = await API.post('/api/auth/login', { email, password });

      // Check if MFA is required
      if (response.data.requiresMFA) {
        setMfaRequired(true);
        setMfaTicket(response.data.mfaTicket);
      } else {
        finishLogin(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = (data: { user: any; token: string }) => finishLogin(data);

  const handleMFACancel = () => {
    // User cancelled MFA, return to login form
    setMfaRequired(false);
    setMfaTicket('');
    setEmail('');
    setPassword('');
  };

  // If MFA is required, show MFA verification component
  if (mfaRequired) {
    return (
      <MFAVerification
        ticket={mfaTicket}
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />
    );
  }


  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* IPRI Logo */}
          <div className="flex justify-center mb-8 bg-gray-50 dark:bg-gray-900 rounded-lg p-6">
            <img
              src="/ipri-logo.png"
              alt="IPRI - Indigenous Peoples Rights International"
              className="h-32 w-auto max-w-full object-contain"
            />
          </div>
          <h2 className="mt-6 text-center text-2xl font-bold text-gray-900 dark:text-gray-100 font-heading">
            Case Management System
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Sign in to access your account
          </p>
        </div>
        {expired && !error && (
          <div role="status" className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
            Your session has ended. Sign in again and you'll be taken back to where you were.
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 dark:bg-gray-800 rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 text-gray-900 dark:text-gray-100 dark:bg-gray-800 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-800">{error}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;